import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectionListChange } from '@angular/material/list';
import { MatSelectChange } from '@angular/material/select';
import { AuthService } from '../../services/auth.service';
import { MetadataService } from '../../services/metadata.service';
import { BasicBill, Bill } from '../../types/bills/Bill';
import {
    MetadataObject,
    MetadataItem,
    MetadataObjectValue,
} from '../../types/metadata/MetadataObject';
import _ from 'lodash';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, Subscription, takeUntil } from 'rxjs';
import { BillService } from '../../services/bill.service';
import { DestroyRxComponent } from '../../../components/destroy-rx/destroy-rx.component';

@Component({
    selector: 'las-tracking-data-modal',
    templateUrl: './tracking-data-modal.component.html',
    styleUrls: ['./tracking-data-modal.component.scss'],
    standalone: false,
})
export class TrackingDataModalComponent
    extends DestroyRxComponent
    implements OnInit, OnDestroy
{
    bill!: Bill;
    trackingData!: MetadataObject[];
    objects: MetadataObject[] = [];

    selected: MetadataObject | null = null;

    copyValues: MetadataObjectValue[] = <MetadataObjectValue[]>[];

    stringForm: FormGroup = new FormGroup({
        valueString: new FormControl<string>(''),
    });
    stringFormSubscription: Subscription;

    valueObject: MetadataObjectValue = <MetadataObjectValue>{};
    valueString: string = '';
    valueInt?: number;
    valueBill: BasicBill = <BasicBill>{};

    billSearchResults: BasicBill[] = <BasicBill[]>[];

    searchForm: FormGroup = new FormGroup({
        searchFilter: new FormControl<string>(''),
    });
    searchFormSubscription: Subscription;
    searchFilter: string = '';

    isLoading: boolean = false;
    isSaving: boolean = false;
    isValid: boolean = false;

    constructor(
        private dialog: MatDialogRef<TrackingDataModalComponent>,
        private metadataService: MetadataService,
        private billService: BillService,
        private auth: AuthService,
        @Inject(MAT_DIALOG_DATA) data: ModalData,
    ) {
        super();

        this.bill = data.bill;
        this.trackingData = data.trackingData;

        this.searchFormSubscription = this.searchForm.valueChanges
            .pipe(debounceTime(400))
            .pipe(takeUntil(this.destroy$))
            .subscribe((changes) => {
                this.searchFilter = changes.searchFilter;
            });

        this.stringFormSubscription = this.stringForm.valueChanges
            .pipe(debounceTime(400))
            .pipe(takeUntil(this.destroy$))
            .subscribe((changes) => {
                this.valueString = changes.valueString;
                this.isValid = this.valueString !== '';
            });
    }

    /**
     * Loads both lists if the user is an admin; otherwise just the personal list.
     */
    ngOnInit(): void {
        this.getObjects();
    }

    /*
     * Returns true if the lists have elements; false, otherwise.
     */
    hasData = () => this.trackingData.length > 0;

    /*
     * Populate Tracking Data objects dropdown
     */
    getObjects() {
        this.metadataService.getObjects('Bill').subscribe((result) => {
            this.objects = this.removeExistingObjs(result);
        });
    }

    /*
     * Load tracking data for target
     */
    loadData() {
        this.isLoading = true;

        this.metadataService
            .getBillItems(this.bill.billId)
            .subscribe((data) => {
                this.trackingData = this.parseJson(data);
                this.isLoading = false;
            });
    }

    /*
     * Remove Item from being tracked
     */
    removeItem(item: MetadataItem, obj: MetadataObject) {
        if (
            confirm(
                `Are you sure you want to remove the value '${item.dynamicValue}' from ${obj.name}?`,
            )
        ) {
            this.isSaving = true;

            this.metadataService.removeItem(item).subscribe((result) => {
                this.resetValues();
                this.loadData();
                this.isSaving = false;
            });
        }
    }

    /*
     * Handle Tracking Data Object change
     */
    onChange(obj: MetadataObject) {
        this.resetValues();

        this.selected = obj;

        if (this.selected?.holdsValues) {
            this.metadataService.getObject(obj.id).subscribe((result) => {
                this.selected = result;

                this.selected.values.forEach((value) => {
                    value.active = false;
                    this.trackingData.forEach((data) => {
                        if (
                            data.id == obj.id &&
                            data.objectType.name == 'MultiList'
                        ) {
                            data.items.forEach((item) => {
                                if (item.objectValueID == value.id) {
                                    value.active = true;
                                }
                            });
                        }
                    });
                });

                this.copyValues = _.cloneDeep(result.values);
            });
        }
    }

    /*
     * Search Bills for Bill Reference
     */
    search() {
        const searchText = this.searchForm.value['searchFilter'];

        this.billService.searchBills(searchText).subscribe((result) => {
            this.billSearchResults = result;
        });
    }

    /*
     * SelectList option change
     */
    onSelectChange(event: MatSelectChange) {
        this.isValid = typeof this.valueObject?.id !== 'undefined';
    }

    /*
     * MultiList option change
     */
    onSelectionChange(event: MatSelectionListChange) {
        if (!this.selected) return;

        let item = event.options[0].value;
        let value = this.selected.values.find((value) => value.id == item.id);

        value!.active = event.options[0].selected;

        this.isValid = !_.isEqual(this.selected.values, this.copyValues);
    }

    /*
     * Checkbox option change
     */
    onCheckBoxChange(event: MatSelectChange) {
        this.isValid = typeof this.valueInt !== 'undefined';
    }

    /*
     * Bill Search Selection change
     */
    selectBill(item: BasicBill) {
        this.valueBill = item;

        this.isValid = true;
    }

    /*
     * Apply new metadata item to bill
     */
    applyItem() {
        if (!this.selected) return;

        let item: MetadataItem = <MetadataItem>{
            targetID: this.selected.target.id,
            targetStringID: this.bill.billId,
            objectID: this.selected.id,
            objectValue: this.valueObject,
        };

        item.targetStringID = this.bill.billId;

        if (this.selected.objectType.name == 'BillReference') {
            item.valueJson = JSON.stringify(this.valueBill);
            item.valueString = this.valueBill.measure;
        } else if (this.selected.objectType.name == 'MultiList') {
            item.objectValues = this.selected.values;
        } else if (this.selected.objectType.name == 'CheckBox') {
            item.valueInt = this.valueInt!;
        } else {
            item.valueString = this.valueString;
        }

        this.isSaving = true;

        this.metadataService.saveItem(item).subscribe((_) => {
            this.resetValues();

            this.loadData();
            this.isSaving = false;
        });
    }

    /*
     * Reset selected values
     */
    private resetValues() {
        this.isValid = false;

        this.searchForm.controls['searchFilter'].setValue('');

        this.billSearchResults = <BasicBill[]>[];
        this.valueBill = <BasicBill>{};

        this.valueObject = <MetadataObjectValue>{};
        this.valueInt = undefined;
        this.valueString = '';
        this.selected = null;
    }

    /*
     * Parse JSON string into object
     */
    private parseJson(objs: MetadataObject[]) {
        objs.forEach(function (obj) {
            obj.items.forEach(function (item) {
                if (item.valueJson) {
                    item.valueJson = JSON.parse(item.valueJson);
                }
            });
        });

        return objs;
    }

    /*
     * Remove applied items from MultiList to prevent duplicate items
     */
    private removeExistingObjs(objs: MetadataObject[]): MetadataObject[] {
        let copy: MetadataObject[] = <MetadataObject[]>[];

        copy = Object.assign(copy, objs);

        objs.forEach((obj) => {
            if (obj.holdsValues && obj.objectType.name != 'MultiList') {
                this.trackingData.forEach((item) => {
                    if (item.id == obj.id) {
                        let index = copy.indexOf(obj);

                        if (index > -1) {
                            copy.splice(index, 1);
                        }
                    }
                });
            }
        });

        return objs;
    }
}

type ModalData = {
    bill: Bill;
    trackingData: MetadataObject[];
};
